import {
  useApiClient,
  useShoppingCartRemoveVoucher,
  WebshopShoppingCartCommands,
  WebshopShoppingCartEvent,
  WebshopShoppingCartQueries,
  WebshopShoppingCartVersioned,
  webshopShoppingCartRemoveProductService,
} from '@cldsystems/ws-api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { UpdateMutateProps } from '@/types/common';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import {
  getApiErrorMessage,
  isNoChangesError,
  isOptimisticLockError,
} from '@/utils/get-api-error-message';
import { priceCheck } from '@/utils/price-check';

type ShoppingCartProduct = WebshopShoppingCartVersioned['shoppingCart']['products'][number];

type ShoppingCartRemoveProductMutationPayload = {
  productId: string;
  event: WebshopShoppingCartEvent;
  product: WithQuantity<SimpleProductInfo>;
  operationId: string;
  buttonType?: MyCartActionButtonsEnum;
  optimisticApplied?: boolean;
  previousProduct?: ShoppingCartProduct;
  /** Skip the error toast for a (retryable) optimistic-lock conflict. */
  suppressVersionConflictToast?: boolean;
};

type ShoppingCartRemoveProductMutationContext = {
  shoppingCartId: string;
  productId: string;
  operationId: string;
  previousProduct?: ShoppingCartProduct;
  previousVersion?: number;
};

const useShoppingCartRemoveProductMutate = (
  markMyCartPending: (
    productId: string,
    buttonType: MyCartActionButtonsEnum | undefined,
    operationId: string,
  ) => void,
  clearMyCartPending: (productId: string, operationId?: string) => void,
  getMyCartPending: (productId: string) =>
    | {
        operationId: string;
      }
    | undefined,
  setIsPendingUpdateMyCartProductId: (id: string | undefined) => void,
  setActionButtonType: (buttonType: MyCartActionButtonsEnum | undefined) => void,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { httpClient, hasSchema } = useApiClient();
  const shoppingCartRemoveVoucher = useShoppingCartRemoveVoucher();

  return useMutation<
    { id: string },
    AxiosError,
    UpdateMutateProps<ShoppingCartRemoveProductMutationPayload>,
    ShoppingCartRemoveProductMutationContext | undefined
  >({
    mutationKey: [WebshopShoppingCartCommands.RemoveProduct],
    mutationFn: ({
      params,
      payload,
    }: UpdateMutateProps<ShoppingCartRemoveProductMutationPayload>) =>
      webshopShoppingCartRemoveProductService({
        httpClient,
        hasSchema: Boolean(hasSchema),
      })({ params, payload: { productId: payload!.productId, event: payload!.event } }),
    onMutate: async ({ params, payload }) => {
      if (!payload?.product || payload.productId !== payload.product.id) return;

      markMyCartPending(payload.productId, payload.buttonType, payload.operationId);
      setIsPendingUpdateMyCartProductId(payload.productId);
      setActionButtonType(payload.buttonType);

      const queryKey = [WebshopShoppingCartQueries.GetById, params.id];
      await queryClient.cancelQueries({ queryKey });
      const previousShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(queryKey);
      const previousProduct =
        payload.previousProduct ??
        previousShoppingCart?.shoppingCart.products.find(
          product => product.id === payload.productId,
        );

      queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
        if (!old) return undefined;

        const products = payload.optimisticApplied
          ? old.shoppingCart.products
          : old.shoppingCart.products.filter(p => p.id !== payload.productId);
        const newAmount = products.reduce(
          (acc, current) => acc + current.price * current.quantity,
          0,
        );
        const newDiscountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );

        return {
          ...old,
          version: old.version + 1,
          shoppingCart: {
            ...old.shoppingCart,
            amount: priceCheck(newAmount),
            discountedAmount: priceCheck(newDiscountedAmount),
            totalAmount: priceCheck(newDiscountedAmount),
            products,
          },
        };
      });

      return {
        shoppingCartId: params.id,
        productId: payload.productId,
        operationId: payload.operationId,
        previousProduct,
        previousVersion: previousShoppingCart?.version,
      } satisfies ShoppingCartRemoveProductMutationContext;
    },
    onError: async (err, variables, context) => {
      const { params } = variables;
      if (context) {
        const currentPending = getMyCartPending(context.productId);

        if (!currentPending || currentPending.operationId === context.operationId) {
          const queryKey = [WebshopShoppingCartQueries.GetById, context.shoppingCartId];
          queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
            if (!old || !context.previousProduct) {
              return old;
            }

            const isProductExists = old.shoppingCart.products.some(
              product => product.id === context.productId,
            );

            if (isProductExists) {
              return old;
            }

            const products = [...old.shoppingCart.products, context.previousProduct];
            const newAmount = products.reduce(
              (acc, current) => acc + current.price * current.quantity,
              0,
            );
            const newDiscountedAmount = products.reduce(
              (acc, current) => acc + current.discountedPrice * current.quantity,
              0,
            );

            return {
              ...old,
              version: context.previousVersion ?? old.version,
              shoppingCart: {
                ...old.shoppingCart,
                amount: priceCheck(newAmount),
                discountedAmount: priceCheck(newDiscountedAmount),
                totalAmount: priceCheck(newDiscountedAmount),
                products,
              },
            };
          });
        }
      }
      // No-op commands ("no changes detected") are harmless. Optimistic-lock
      // conflicts are retried with the real version by the caller; stay silent
      // on the first failure so we only notify if the retry also fails.
      const isSuppressedVersionConflict =
        variables.payload?.suppressVersionConflictToast && isOptimisticLockError(err);
      if (!isNoChangesError(err) && !isSuppressedVersionConflict) {
        toast.error(getApiErrorMessage(err, t?.common.productRemoveFromMyCartFailed));
      }
      const data = err.response?.data as { code?: number } | undefined;
      if (err.response?.status === 409 && data?.code === 1) {
        await shoppingCartRemoveVoucher.mutateAsync({ params });
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
    },
    retry: (failureCount, err) => {
      const data = err.response?.data as { code?: number } | undefined;
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
      if (failureCount < 1 && err.response?.status === 409 && data?.code === 1) {
        return true;
      }

      return false;
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) {
        clearMyCartPending(context.productId, context.operationId);
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
    },
  });
};

export default useShoppingCartRemoveProductMutate;
